// M3 Dodge Tilt — tilt head left or right to dodge balls flying at the camera.
// Spec: /microgames/03-dodge-tilt.md

import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
} from '@pose-game/core';

// ─── Constants ──────────────────────────────────────────────────────────

const DURATION_SEC = 22;
const BALL_COUNT = 6;
const FIRST_SPAWN_SEC = 1.5;
const SPAWN_INTERVAL_SEC = 3;
/** Time from spawn to impact (scale-up window). */
const TRAVEL_SEC = 1.4;
/** Telegraph arrow appears this many seconds before impact. */
const TELEGRAPH_LEAD_SEC = 0.8;
/** Minimum signed head-tilt magnitude to count as a dodge (radians). */
const TILT_THRESHOLD_RAD = 0.35;
const WIN_DODGES = 5;

type Side = 'left' | 'right';

interface Ball {
  side: Side;
  /** Game-clock time at which the ball appears (small dot). */
  spawnTime: number;
  /** Game-clock time at which the ball reaches impact size. */
  impactTime: number;
  /** Has the impact moment been resolved? */
  resolved: boolean;
  /** true = dodged, false = hit. Only valid once resolved. */
  dodged: boolean;
}

// ─── Microgame ──────────────────────────────────────────────────────────

export const dodgeTilt: Microgame = (() => {
  let gameCtx: GameContext | null = null;
  let elapsed = 0;
  let balls: Ball[] = [];
  let dodges = 0;
  let hits = 0;
  let currentTilt = 0;
  let ended = false;
  let lastImpactFlashTime = -1;
  let lastImpactWasDodge = false;

  function buildBalls(rng: () => number): Ball[] {
    const out: Ball[] = [];
    for (let i = 0; i < BALL_COUNT; i++) {
      const spawnTime = FIRST_SPAWN_SEC + i * SPAWN_INTERVAL_SEC;
      const side: Side = rng() < 0.5 ? 'left' : 'right';
      out.push({
        side,
        spawnTime,
        impactTime: spawnTime + TRAVEL_SEC,
        resolved: false,
        dodged: false,
      });
    }
    return out;
  }

  /** Is the current `currentTilt` safe for a ball coming from `side`? */
  function tiltIsSafe(side: Side, tilt: number): boolean {
    // ball.side == 'left' → require head tilted to player's right (positive)
    // ball.side == 'right' → require head tilted to player's left (negative)
    return side === 'left'
      ? tilt > TILT_THRESHOLD_RAD
      : tilt < -TILT_THRESHOLD_RAD;
  }

  function resolveBall(ball: Ball): void {
    ball.resolved = true;
    ball.dodged = tiltIsSafe(ball.side, currentTilt);
    if (ball.dodged) {
      dodges += 1;
      gameCtx?.audio.pop();
    } else {
      hits += 1;
      gameCtx?.audio.thud();
    }
    lastImpactFlashTime = elapsed;
    lastImpactWasDodge = ball.dodged;
  }

  // ─── Render helpers ───────────────────────────────────────────────────

  function drawBackground(g: CanvasRenderingContext2D, w: number, h: number) {
    g.fillStyle = '#0a0b12';
    g.fillRect(0, 0, w, h);
    // Radial vignette centered.
    const cx = w / 2;
    const cy = h / 2;
    const maxR = Math.hypot(cx, cy);
    const grad = g.createRadialGradient(cx, cy, maxR * 0.1, cx, cy, maxR);
    grad.addColorStop(0, 'rgba(40, 44, 68, 0.45)');
    grad.addColorStop(0.6, 'rgba(10, 11, 18, 0.6)');
    grad.addColorStop(1, 'rgba(0, 0, 0, 0.95)');
    g.fillStyle = grad;
    g.fillRect(0, 0, w, h);
  }

  function drawBall(
    g: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
  ) {
    const grad = g.createRadialGradient(
      cx - radius * 0.35,
      cy - radius * 0.35,
      radius * 0.1,
      cx,
      cy,
      radius,
    );
    grad.addColorStop(0, '#ffd27a');
    grad.addColorStop(0.45, '#ff8a3d');
    grad.addColorStop(1, '#c2261a');
    g.fillStyle = grad;
    g.beginPath();
    g.arc(cx, cy, radius, 0, Math.PI * 2);
    g.fill();

    // Subtle dark outline so it pops against the vignette.
    g.lineWidth = Math.max(1, radius * 0.04);
    g.strokeStyle = 'rgba(60, 10, 10, 0.65)';
    g.stroke();
  }

  function drawTelegraphArrow(
    g: CanvasRenderingContext2D,
    w: number,
    h: number,
    side: Side,
    pulse: number,
  ) {
    const cy = h / 2;
    const arrowH = h * 0.18;
    const arrowW = w * 0.06;
    const margin = w * 0.025;
    const alpha = 0.55 + 0.45 * pulse;
    g.save();
    g.globalAlpha = alpha;
    g.fillStyle = side === 'left' ? '#ff5577' : '#55aaff';
    g.beginPath();
    if (side === 'left') {
      // Arrow on the LEFT edge pointing left (warning: ball from left).
      const xTip = margin;
      const xBase = margin + arrowW;
      g.moveTo(xTip, cy);
      g.lineTo(xBase, cy - arrowH / 2);
      g.lineTo(xBase, cy + arrowH / 2);
    } else {
      const xTip = w - margin;
      const xBase = w - margin - arrowW;
      g.moveTo(xTip, cy);
      g.lineTo(xBase, cy - arrowH / 2);
      g.lineTo(xBase, cy + arrowH / 2);
    }
    g.closePath();
    g.fill();
    g.restore();
  }

  function drawTiltIndicator(
    g: CanvasRenderingContext2D,
    w: number,
    h: number,
    tilt: number,
    safe: boolean,
  ) {
    const cx = w / 2;
    const cy = h * 0.82;
    const r = Math.min(w, h) * 0.04;
    g.save();
    g.translate(cx, cy);
    // Clamp tilt for visual purposes.
    const clamped = Math.max(-1, Math.min(1, tilt / (Math.PI / 4)));
    g.rotate(clamped * (Math.PI / 4));
    // Head circle
    g.fillStyle = safe ? '#3ddb84' : '#ff5566';
    g.beginPath();
    g.arc(0, 0, r, 0, Math.PI * 2);
    g.fill();
    // Eyes
    g.fillStyle = '#0a0b12';
    g.beginPath();
    g.arc(-r * 0.35, -r * 0.15, r * 0.12, 0, Math.PI * 2);
    g.arc(r * 0.35, -r * 0.15, r * 0.12, 0, Math.PI * 2);
    g.fill();
    // Mouth
    g.strokeStyle = '#0a0b12';
    g.lineWidth = Math.max(1, r * 0.1);
    g.beginPath();
    g.arc(0, r * 0.15, r * 0.35, 0, Math.PI);
    g.stroke();
    g.restore();

    // Safe-zone labels.
    g.fillStyle = '#7d8298';
    g.font = `${Math.round(h * 0.022)}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('tilt to dodge', cx, cy + r + h * 0.04);
  }

  function drawHud(
    g: CanvasRenderingContext2D,
    w: number,
    h: number,
    remaining: number,
  ) {
    g.fillStyle = '#e6e8f5';
    g.font = `bold ${Math.round(h * 0.04)}px system-ui, sans-serif`;
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillText(`dodged: ${dodges} / ${BALL_COUNT}`, w * 0.04, h * 0.04);

    g.textAlign = 'right';
    g.fillText(`${Math.max(0, remaining).toFixed(1)}s`, w * 0.96, h * 0.04);

    if (hits > 0) {
      g.textAlign = 'left';
      g.fillStyle = '#ff8899';
      g.font = `${Math.round(h * 0.028)}px system-ui, sans-serif`;
      g.fillText(`hits: ${hits}`, w * 0.04, h * 0.1);
    }
  }

  function drawImpactFlash(
    g: CanvasRenderingContext2D,
    w: number,
    h: number,
  ) {
    if (lastImpactFlashTime < 0) return;
    const since = elapsed - lastImpactFlashTime;
    const FLASH_DUR = 0.25;
    if (since < 0 || since > FLASH_DUR) return;
    const alpha = (1 - since / FLASH_DUR) * 0.35;
    g.fillStyle = lastImpactWasDodge
      ? `rgba(61, 219, 132, ${alpha})`
      : `rgba(255, 85, 102, ${alpha})`;
    g.fillRect(0, 0, w, h);
  }

  // ─── Microgame contract ───────────────────────────────────────────────

  return {
    name: '03-dodge-tilt',
    durationSec: DURATION_SEC,
    requires: { face: true },

    init(c: GameContext) {
      gameCtx = c;
      elapsed = 0;
      dodges = 0;
      hits = 0;
      currentTilt = 0;
      ended = false;
      lastImpactFlashTime = -1;
      lastImpactWasDodge = false;
      balls = buildBalls(c.rng);
    },

    tick(dt: number, frame: InputFrame) {
      if (ended) return;
      elapsed += dt;
      currentTilt = frame.face.visible ? frame.face.headTilt : 0;

      // Resolve any ball whose impactTime has just been crossed.
      for (const ball of balls) {
        if (ball.resolved) continue;
        if (elapsed >= ball.impactTime) {
          resolveBall(ball);
        }
      }
    },

    render(g: CanvasRenderingContext2D) {
      if (gameCtx == null) return;
      const { canvas } = gameCtx;
      const w = canvas.width;
      const h = canvas.height;

      drawBackground(g, w, h);

      const cxCanvas = w / 2;
      const cyCanvas = h / 2;
      const impactRadius = h * 0.2; // ~40% canvas height diameter.
      const minRadius = Math.max(2, h * 0.005);

      // Draw active balls and telegraph arrows.
      for (const ball of balls) {
        if (ball.resolved) continue;
        if (elapsed < ball.spawnTime) continue; // not visible yet

        const t = (elapsed - ball.spawnTime) / TRAVEL_SEC;
        const tClamped = Math.max(0, Math.min(1, t));
        // Ease-in so the ball seems to accelerate toward the camera.
        const eased = tClamped * tClamped;
        const radius = minRadius + (impactRadius - minRadius) * eased;

        // Ball drifts laterally from its side toward the centre as it grows.
        const startX =
          ball.side === 'left' ? w * 0.32 : w * 0.68;
        const ballX = startX + (cxCanvas - startX) * eased;
        const ballY = cyCanvas;
        drawBall(g, ballX, ballY, radius);

        const timeToImpact = ball.impactTime - elapsed;
        if (timeToImpact <= TELEGRAPH_LEAD_SEC && timeToImpact >= 0) {
          const pulse = 0.5 + 0.5 * Math.sin(elapsed * 14);
          drawTelegraphArrow(g, w, h, ball.side, pulse);
        }
      }

      drawImpactFlash(g, w, h);

      // Tilt indicator: green if current tilt would dodge a *current threat*,
      // otherwise green when player is in any safe zone, red otherwise.
      const threatSide = currentThreatSide();
      const safe =
        threatSide != null
          ? tiltIsSafe(threatSide, currentTilt)
          : Math.abs(currentTilt) > TILT_THRESHOLD_RAD;
      drawTiltIndicator(g, w, h, currentTilt, safe);

      drawHud(g, w, h, DURATION_SEC - elapsed);
    },

    end(): MicrogameResult {
      ended = true;
      const won = dodges >= WIN_DODGES;
      if (gameCtx) {
        if (won) gameCtx.audio.win();
        else gameCtx.audio.lose();
      }
      return {
        microgame: '03-dodge-tilt',
        won,
        score: dodges,
        detail: { hits },
      };
    },
  };

  /** The side of the next un-resolved ball within the telegraph window, if any. */
  function currentThreatSide(): Side | null {
    for (const ball of balls) {
      if (ball.resolved) continue;
      const timeToImpact = ball.impactTime - elapsed;
      if (timeToImpact <= TELEGRAPH_LEAD_SEC && timeToImpact >= 0) {
        return ball.side;
      }
    }
    return null;
  }
})();
